// ── Modules activables (Organization.modules) ──────────────────────
// Les pages d'un module éteint restent dans le code : elles sont retirées
// des menus et leurs URL renvoient vers l'accueil. Rallumage : Réglages > Modules.
import { ReactNode, useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import api from './api'
import { employeeApi } from './employeeApi'

export type ModuleKey = 'rh' | 'conges' | 'badgeuse' | 'assistant' | 'planningAuto'
export type Modules = Record<ModuleKey, boolean>

const ALL_ON: Modules = { rh: true, conges: true, badgeuse: true, assistant: true, planningAuto: true }
// Pendant le tout premier chargement (sans mémoire locale) on n'affiche rien d'optionnel :
// évite d'appeler une seconde les écrans d'un module éteint.
const ALL_OFF: Modules = { rh: false, conges: false, badgeuse: false, assistant: false, planningAuto: false }

// Mémoire locale pour éviter le clignotement des menus au chargement
function readCache(key: string): Modules | null {
  try {
    const raw = localStorage.getItem(key)
    return raw ? { ...ALL_ON, ...JSON.parse(raw) } : null
  } catch { return null }
}
function writeCache(key: string, m: Modules) {
  try { localStorage.setItem(key, JSON.stringify(m)) } catch { /* ignore */ }
}

function useModulesFrom(cacheKey: string, load: () => Promise<Modules>) {
  const [modules, setModules] = useState<Modules | null>(() => readCache(cacheKey))
  useEffect(() => {
    let alive = true
    load()
      .then(m => { if (alive) { const v = { ...ALL_ON, ...m }; setModules(v); writeCache(cacheKey, v) } })
      .catch(() => { if (alive && !modules) setModules(ALL_ON) })
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return { modules: modules ?? ALL_OFF, loaded: modules !== null }
}

/** Modules vus par un gérant (appli web + appli mobile gérant) */
export function useModules() {
  return useModulesFrom('modules_manager', () =>
    api.get('/organization/modules').then(r => r.data.modules as Modules))
}

/** Modules vus par un employé (appli mobile employé) */
export function useEmployeeModules() {
  return useModulesFrom('modules_employee', () =>
    employeeApi.get('/employee/me/modules').then(r => r.data.modules as Modules))
}

/** Garde de route gérant : si le module est éteint, retour à l'accueil */
export function ModuleRoute({ module, redirect = '/dashboard', children }: {
  module: ModuleKey; redirect?: string; children: ReactNode
}) {
  const { modules, loaded } = useModules()
  if (!loaded) return null
  return modules[module] ? <>{children}</> : <Navigate to={redirect} replace />
}

/** Garde de route employé (appli mobile) */
export function EmployeeModuleRoute({ module, redirect = '/app/home', children }: {
  module: ModuleKey; redirect?: string; children: ReactNode
}) {
  const { modules, loaded } = useEmployeeModules()
  if (!loaded) return null
  return modules[module] ? <>{children}</> : <Navigate to={redirect} replace />
}
